import React, { useEffect, useRef, useState } from 'react';

import { Spinner } from '@flixbus/honeycomb-react';

import Scroller from 'common/Scroller';
import WorkAssigmentTag from 'pages/RideListPage/components/SegmentTag';

import { computeVisibleCount } from './computeVisibleCount';
import { SEQUENCE_TAGS_TEXT } from './texts';

import styles from './styles.module.css';

const SequenceTags = ({
	segments,
	timezone,
	rideId,
	line,
	tripNumber,
	departure,
	safetyPx = 1,
}) => {
	const [expanded, setExpanded] = useState(false);
	const [measuring, setMeasuring] = useState(true);
	// Start at full length — measurement collapses after first rAF
	const [visibleCount, setVisibleCount] = useState(segments?.length || 0);
	const [hiddenCount, setHiddenCount] = useState(0);
	const [singleScroll, setSingleScroll] = useState(false);

	const hostRef = useRef(null);
	const mirrorRef = useRef(null);
	const plusRef = useRef(null);
	const last = useRef({ visible: -1, hidden: -1, scroll: false });

	const commit = (visible, hidden, scroll) => {
		const prev = last.current;
		if (prev.visible === visible && prev.hidden === hidden && prev.scroll === scroll) {
			setMeasuring(false);
			return;
		}
		last.current = { visible, hidden, scroll };
		setVisibleCount(visible);
		setHiddenCount(hidden);
		setSingleScroll(scroll);
		setMeasuring(false);
	};

	const getHostContentWidth = () => {
		const host = hostRef.current;
		if (!host) return 0;
		const cs = getComputedStyle(host);
		const padLR = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
		return Math.max((host.clientWidth || 0) - padLR, 0);
	};

	const measure = () => {
		const n = segments?.length || 0;
		const hostContentW = getHostContentWidth();

		const mirror = mirrorRef.current;
		const gapX = mirror
			? parseFloat(getComputedStyle(mirror).columnGap || getComputedStyle(mirror).gap || '0') || 0
			: 0;

		const els = mirror ? Array.from(mirror.children) : [];
		const widths = els.map((el) => el.getBoundingClientRect().width);

		// Update the +N mirror text so its measured width matches the largest label
		// we might render. Only relevant when we have >1 segments.
		if (n > 1 && plusRef.current) {
			plusRef.current.textContent = SEQUENCE_TAGS_TEXT.plus(n - 1);
		}
		const plusWidth = plusRef.current?.getBoundingClientRect().width || 0;

		const { visible, hidden, scroll } = computeVisibleCount({
			n,
			hostContentW,
			widths,
			gapX,
			plusWidth,
			expanded,
			safetyPx,
		});
		commit(visible, hidden, scroll);
	};

	useEffect(() => {
		setMeasuring(true);
		const host = hostRef.current;
		let ro;
		if (host) {
			ro = new ResizeObserver(() => requestAnimationFrame(measure));
			ro.observe(host);
		}
		const id = requestAnimationFrame(measure);
		return () => {
			ro && ro.disconnect();
			cancelAnimationFrame(id);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [segments, expanded, safetyPx]);

	if (!segments?.length) return null;

	const tagKey = (seg, prefix, index) => `${prefix}-${seg.stations?.[0]?.id}-${index}`;

	const renderTag = (seg, prefix, index) => (
		<WorkAssigmentTag
			key={tagKey(seg, prefix, index)}
			assigment={seg}
			timezone={timezone}
			rideId={rideId}
			line={line}
			tripNumber={tripNumber}
			departure={departure}
		/>
	);

	if (singleScroll && segments.length === 1) {
		return (
			<div ref={hostRef} className={styles.container}>
				<Scroller>
					<div style={{ display: 'flex' }}>
						<WorkAssigmentTag
							key={tagKey(segments[0], 'scroll', 0)}
							assigment={segments[0]}
							timezone={timezone}
							rideId={rideId}
							line={line}
							tripNumber={tripNumber}
							departure={departure}
							hostRef={hostRef}
							useHost={true}
						/>
					</div>
				</Scroller>
			</div>
		);
	}

	return (
		<div>
			<div
				ref={hostRef}
				className={`${styles.container} ${expanded ? styles.containerExpanded : ''}`}
			>
				{measuring ? (
					<div className={styles.spinnerWrapper}>
						<Spinner size="sm" aria-label={SEQUENCE_TAGS_TEXT.loadingAria} />
					</div>
				) : (
					<>
						{(expanded ? segments : segments.slice(0, visibleCount)).map((seg, i) =>
							renderTag(seg, 'tag', i)
						)}
						{!expanded && hiddenCount > 0 && (
							<button type="button" className={styles.more} onClick={() => setExpanded(true)}>
								{SEQUENCE_TAGS_TEXT.plus(hiddenCount)}
							</button>
						)}
					</>
				)}
			</div>

			{/* Offscreen mirror — each tag wrapped in div so children.length === n exactly */}
			<div
				ref={mirrorRef}
				aria-hidden
				className={styles.mirror}
				style={{ position: 'absolute', left: -9999, top: -9999 }}
			>
				{segments.map((seg, index) => (
					<div key={tagKey(seg, 'mirror', index)}>{renderTag(seg, 'mirrorinner', index)}</div>
				))}
			</div>

			{/* +N button measurer */}
			<div
				aria-hidden
				className={styles.mirror}
				style={{ position: 'absolute', left: -9999, top: -9999 }}
			>
				<button ref={plusRef} type="button" className={styles.more}>
					{SEQUENCE_TAGS_TEXT.mirrorPlus}
				</button>
			</div>

			{expanded && hiddenCount > 0 && (
				<button type="button" className={styles.showLess} onClick={() => setExpanded(false)}>
					{SEQUENCE_TAGS_TEXT.showLess}
				</button>
			)}
		</div>
	);
};

export default SequenceTags;
